import React from 'react';
import { CHAIN_POPUP_WIDTH, ReportPopupPosition } from './reportPopupPosition';
import { isCompleteNumber } from '../../../Utils/measurementValueParts';
import { CHAIN_FIELDS } from '../../../Utils/measurementChain';
import type { ChainNotice, ChainSlot } from './useChainEditing';

/**
 * The measurement-value editor, and the note that says what the chain just did.
 *
 * Styling copied verbatim from `ReportsLayout` (1 Sep) so the reports page and the
 * studies page look identical — a clinician moving between the two should not be able to
 * tell which surface they are on.
 */

type ChainFieldPopupProps = {
  slot: ChainSlot | null;
  position: ReportPopupPosition | null;
  draft: string;
  onDraftChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
};

export const ChainFieldPopup: React.FC<ChainFieldPopupProps> = ({
  slot,
  position,
  draft,
  onDraftChange,
  onCommit,
  onCancel,
}) => {
  // Escape unmounts the popup, which blurs the input — without this flag the blur would
  // commit the value the clinician just asked to discard.

  const cancelledRef = React.useRef(false);
  React.useEffect(() => {
    cancelledRef.current = false;
  }, [slot?.key, slot?.from]);

  if (!position || !slot) {
    return null;
  }

  // Freeform fields (EF_Prose) have no "half-typed" state to guard against — any text is
  // a complete value, the same way a plain text field works everywhere else in the report.
  // The `isCompleteNumber` gate below exists only for numeric fields, where "4.7" passes
  // through "4" and "4." on the way and committing either would cascade a wrong figure.
  const isFreeform = !!CHAIN_FIELDS[slot.key]?.freeform;
  const complete = isFreeform || isCompleteNumber(draft);
  const partial = draft.trim() !== '' && !complete;

  /**
   * Clicking away commits, so there is no Save button. Only a COMPLETE number commits:
   * an edit drives `recomputeChain`, and "4.7" passes through "4" and "4." on the way, so
   * a partial value would cascade a wrong figure through everything indexed to it. A
   * partial value is discarded rather than written.
   */
  const commitIfComplete = () => {
    if (cancelledRef.current) {
      return;
    }
    if (complete) {
      onCommit();
    } else {
      onCancel();
    }
  };

  return (
    <div
      className="chain-field-popup"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: `${CHAIN_POPUP_WIDTH}px`,
        maxWidth: 'calc(100% - 32px)',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        color: 'black',
        zIndex: 1000,
        padding: '8px',
      }}
    >
      <div style={{ fontSize: '11px', color: '#5b6b80', marginBottom: '4px', fontWeight: 600 }}>
        {slot.label}
      </div>
      <div style={{ fontSize: '10px', color: '#8494a8', marginBottom: '4px' }}>
        Values calculated from this one update with it.
      </div>
      {/*
        The unit sits OUTSIDE the input, as fixed text. It is deliberately not editable —
        see `measurementValueParts.ts` for why a mangled unit is worse than it looks.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px solid ${partial ? '#d19a2a' : '#ccc'}`,
          borderRadius: '3px',
          padding: '0 6px',
          background: '#fff',
        }}
      >
        <input
          autoFocus
          value={draft}
          onChange={e => onDraftChange(e.target.value)}
          onBlur={commitIfComplete}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              // Incomplete: hold the popup open and wait for the rest of the number.
              if (complete) {
                onCommit();
              }
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelledRef.current = true;
              onCancel();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: '13px',
            padding: '4px 0',
            border: 'none',
            outline: 'none',
          }}
        />
        {slot.unit && (
          <span
            style={{
              fontSize: '12px',
              color: '#8494a8',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            {slot.unit}
          </span>
        )}
      </div>
      <div
        style={{
          marginTop: '5px',
          fontSize: '10px',
          color: partial ? '#b8801a' : '#8494a8',
          minHeight: '13px',
        }}
      >
        {partial ? 'Finish the number to apply it.' : 'Applied when you click away.'}
      </div>
    </div>
  );
};

type ChainNoticeBarProps = {
  notice: ChainNotice | null;
  onDismiss: () => void;
};

/** How long a notice stays up before it clears itself. */
const NOTICE_AUTO_DISMISS_MS = 7000;

export const ChainNoticeBar: React.FC<ChainNoticeBarProps> = ({ notice, onDismiss }) => {
  /**
   * The notice clears itself.
   *
   * Dr Shashi, 06 Sep: *"Every time we have to dismiss. Rather then saving time it's
   * causing more time consumption"*. A notice that reports something already done has no
   * business demanding a click before the report can be read — the X stays for anyone who
   * wants it gone sooner.
   *
   * ⚠️ Keyed on WHETHER a notice is showing, never on WHAT it says — see the same guard in
   * `ReportsLayout`. Depending on the text meant every re-announcement from the chain's
   * 600 ms debounce cancelled the pending timeout and armed a new one, so the countdown
   * was pushed back faster than it could elapse and the notice never went away.
   *
   * `onDismiss` is read through a ref rather than depended on: the caller passes a fresh
   * arrow function on every render, so depending on it would reintroduce exactly the reset
   * loop this is fixing.
   */
  const dismissRef = React.useRef(onDismiss);
  dismissRef.current = onDismiss;
  const noticeVisible = notice !== null;
  React.useEffect(() => {
    if (!noticeVisible) {
      return undefined;
    }
    const timer = setTimeout(() => dismissRef.current?.(), NOTICE_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [noticeVisible]);

  if (!notice) {
    return null;
  }

  /**
   * A floating toast, NOT part of the document.
   *
   * `ReportsLayout` renders its version as `position: sticky; bottom: 0`, which works
   * there because the report sits in a side panel with its own scroll box. On this page
   * the report IS the page, so sticky-bottom put the note inside the report's own flow —
   * it landed across the Recommendations heading and read as if the yellow box were part
   * of the report (Kevin, 1 Sep: "this dismiss banner coming below is looking shit").
   *
   * Fixed to the bottom-right corner instead: never covers the body text, never mistaken
   * for report content, and stays put while the clinician scrolls to check the values it
   * is talking about.
   */
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1200,
        width: '380px',
        maxWidth: 'calc(100vw - 48px)',
        boxSizing: 'border-box',
        padding: '10px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        lineHeight: 1.45,
        color: '#0b1f2a',
        backgroundColor: notice.warnings.length ? '#fef3c7' : '#ccfbf1',
        border: `1px solid ${notice.warnings.length ? '#d19a2a' : '#2dd4bf'}`,
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {notice.warnings.map(message => (
            <div key={message} style={{ fontWeight: 600, marginBottom: '4px' }}>
              {message}
            </div>
          ))}
          {notice.changed.length > 0 && <div>Updated: {notice.changed.join(', ')}.</div>}
        </div>
        <div
          onClick={onDismiss}
          role="button"
          aria-label="Dismiss"
          title="Dismiss"
          style={{
            flexShrink: 0,
            cursor: 'pointer',
            fontSize: '16px',
            lineHeight: '16px',
            color: '#5b6b80',
            padding: '0 2px',
            userSelect: 'none',
          }}
        >
          ×
        </div>
      </div>
    </div>
  );
};
